import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Story, useStoryUpdateSubscriptionStatusMutation } from '../../../../services';

interface SetStoryNotifiedArgs {
  storyId: number;
  isNotified: boolean;
}

export const useStoryNotification = () => {
  const { t } = useTranslation();

  const [updateSubscriptionStatus] = useStoryUpdateSubscriptionStatusMutation();

  const [notifiedById, setNotifiedById] = useState<Record<number, boolean>>({});

  const [isNotificationPending, setIsNotificationPending] = useState(false);

  const isStoryNotified = useCallback(
    ({ id, subscriptionStatus }: Story) => notifiedById[id] ?? subscriptionStatus,
    [notifiedById]
  );

  const setStoryNotified = useCallback(
    ({ storyId, isNotified }: SetStoryNotifiedArgs) =>
      setNotifiedById((notifiedByIdPrev) => ({ ...notifiedByIdPrev, [storyId]: isNotified })),
    []
  );

  const toggleStoryNotification = useCallback(
    async (story: Story) => {
      const { id: storyId } = story;

      const status = !isStoryNotified(story);

      setStoryNotified({ storyId, isNotified: status });

      setIsNotificationPending(true);

      const revertStory = () => {
        setStoryNotified({ storyId, isNotified: !status });
        toast.error(t('common.error-message'));
      };

      try {
        const isUpdated = await updateSubscriptionStatus({ storyId, status }).unwrap();

        if (!isUpdated) {
          revertStory();
        }
      } catch (_) {
        revertStory();
      } finally {
        setIsNotificationPending(false);
      }
    },
    [isStoryNotified, setStoryNotified, t, updateSubscriptionStatus]
  );

  return useMemo(
    () => ({ isStoryNotified, setStoryNotified, toggleStoryNotification, isNotificationPending }),
    [isNotificationPending, isStoryNotified, setStoryNotified, toggleStoryNotification]
  );
};
